import { useEffect, useState } from 'react';
import { useInventoryAppServices } from '@/app/services/InventoryAppServicesContext';
import type { LocalPhotoRef } from '@/shared/capture/capture-session';

export function usePhotoUrls(photos: LocalPhotoRef[]) {
  const { photoBlobs: repository } = useInventoryAppServices();
  const [urls, setUrls] = useState<Record<string, { originalUrl: string | null; thumbnailUrl: string | null }>>({});
  const photoSnapshot = JSON.stringify(
    photos.map(photo => ({
      id: photo.id,
      localBlobId: photo.localBlobId,
      thumbnailBlobId: photo.thumbnailBlobId ?? photo.localBlobId,
    })),
  );

  useEffect(() => {
    let isMounted = true;
    const objectUrls: string[] = [];
    const photoInputs = JSON.parse(photoSnapshot) as Array<{
      id: string;
      localBlobId: string;
      thumbnailBlobId: string;
    }>;

    async function loadUrls() {
      const entries = await Promise.all(
        photoInputs.map(async photo => {
          const [original, thumbnail] = await Promise.all([
            repository.get(photo.localBlobId),
            repository.get(photo.thumbnailBlobId),
          ]);
          if (!isMounted) {
            return [photo.id, { originalUrl: null, thumbnailUrl: null }] as const;
          }

          const originalUrl = original ? URL.createObjectURL(original.blob) : null;
          const thumbnailUrl = thumbnail ? URL.createObjectURL(thumbnail.blob) : originalUrl;

          if (originalUrl) {
            objectUrls.push(originalUrl);
          }

          if (thumbnailUrl && thumbnailUrl !== originalUrl) {
            objectUrls.push(thumbnailUrl);
          }

          return [photo.id, { originalUrl, thumbnailUrl }] as const;
        }),
      );

      if (isMounted) {
        setUrls(Object.fromEntries(entries));
      }
    }

    void loadUrls();

    return () => {
      isMounted = false;
      for (const objectUrl of objectUrls) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [photoSnapshot, repository]);

  return urls;
}
